/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntersect } from 'sarsaparilla';
import mapboxgl from 'mapbox-gl';
import { permitSources } from '../constants';
import { getLayersPerService } from '../layers/layers';
import { addMapControls, getControlsPerService } from '../controls/controls';
import { getPopupContentByService } from '../helpers/mapPopup';
import {
    cursorToDefault,
    cursorToPointer,
    addMapLayers,
    addMapSources,
    fitMapBounds,
    trackIsMapExtended,
} from '../helpers/common';
import LoadingSpinner from '../components/LoadingSpinner';
import { featureCollection } from '../turfUtils';

function PermitsMap({
    point,
    points,
    lines,
    polygons,
    highlightedLines,
    config,
    sourceConfig,
    center,
    zoom,
    service,
    mapContainer: mapContainerProp,
    selectedEvent,
    onFullScreen = () => {},
}) {
    const mapContainer = useRef(mapContainerProp || 'map');
    const map = useRef(null);
    const defaultPopup = useRef();
    const latLngClicked = useRef();
    const layerSetup = useRef(
        getLayersPerService({
            service,
            source: Object.keys(sourceConfig || {}).map((geo) => ({
                name: sourceConfig[geo].sourceName,
                isClustered: sourceConfig[geo].isClustered,
            })),
            ignoreLayer: config?.layers?.ignoreLayer,
        })
    );
    const mergedFeatures = useRef([]);
    const fullScreenToggle = useRef(false);
    const basicControls = useRef(null);
    const basicControlsList = useRef(getControlsPerService(service));

    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [isMapExtended, setIsMapExtended] = useState(false);

    const [ref, entry] = useIntersect({ rootMargin: '100px' });

    useEffect(() => {
        if (!map.current) return;
        const mapElement = document.querySelector('.mapboxgl-ctrl-fullscreen');
        mapElement?.addEventListener('click', () => {
            fullScreenToggle.current = !fullScreenToggle.current;
            onFullScreen(fullScreenToggle.current);
        });
    }, [isMapLoaded]);

    useEffect(() => {
        if (!map.current || !basicControlsList.current?.gesture) return;
        map.current._cooperativeGestures = isMapExtended
            ? false
            : basicControlsList.current?.gesture;
    }, [isMapExtended]);

    useEffect(() => {
        if (!map.current) return;

        mergedFeatures.current = [
            ...(point?.features || []),
            ...(points?.features || []),
            ...(lines?.features || []),
            ...(polygons?.features || []),
            ...(highlightedLines?.features || []),
        ].filter((f) => !!f);

        if (!mergedFeatures.current?.length) return;
        const mergedSource = featureCollection(mergedFeatures.current);

        const mergedSize = mergedFeatures.current.length;
        const pointSize = point?.features?.length;
        const pointsSize = points?.features?.length;

        if (mergedSize === 1 && (pointSize || pointsSize)) {
            fitMapBounds(map, mergedSource, 13);
        } else {
            fitMapBounds(map, mergedSource);
        }
    }, [isMapLoaded, point, points, lines, polygons, highlightedLines]);

    const addMapEvents = () => {
        map.current.on('load', () => {
            setIsMapLoaded(true);

            addMapSources(map, sourceConfig);
            addMapLayers(map, layerSetup);

            // eslint-disable-next-line no-unused-expressions
            layerSetup.current?.layers.forEach((layer) => {
                map.current.on('mouseenter', layer.id, () => cursorToPointer(map));
                map.current.on('mouseleave', layer.id, () => cursorToDefault(map));
            });
        });

        // Track double click
        let clicks = 0;

        map.current.on('click', (e) => {
            latLngClicked.current = [e.lngLat.lng, e.lngLat.lat];
            clicks = 0;
            if (selectedEvent) {
                selectedEvent(latLngClicked.current);
            }
            setTimeout(() => {
                if (!clicks) {
                    const features = map.current.queryRenderedFeatures(e.point, {
                        layers: layerSetup.current?.layers?.map((layer) => layer?.id),
                    });
                    const feature = features?.length ? features[0] : null;
                    setSelectedFeature(feature);
                }
            }, 250);
        });

        map.current.on('dblclick', () => {
            clicks += 1;
        });
    };

    const initMap = () => {
        if (!map.current) return;
        basicControlsList.current.ignoreFullScreen =
            config?.mapBasicControls?.ignoreFullscreen;
        basicControls.current = addMapControls(map, basicControlsList.current);

        if (basicControlsList.current?.gesture) {
            trackIsMapExtended(basicControls.current, setIsMapExtended);
        }

        addMapEvents();
    };

    // Initiates map if it was not initiated already
    useEffect(() => {
        if (!map.current && entry?.isIntersecting) {
            mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN;
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: process.env.MAPBOX_STYLE,
                center,
                zoom,
                attributionControl: false,
                cooperativeGestures: basicControlsList.current?.gesture,
                preserveDrawingBuffer: true,
            });
            initMap();
        }
    }, [entry?.isIntersecting]);

    useEffect(() => {
        if (highlightedLines?.features?.length) {
            const isPoint = highlightedLines?.features[0]?.properties.type === 'Point';
            fitMapBounds(map, highlightedLines, isPoint ? 16 : 12);
            return;
        }

        if (!map.current || !center?.length || !zoom) return;
        map.current.jumpTo({ center, zoom });
    }, [center, zoom, highlightedLines]);

    const removePopup = () => {
        defaultPopup.current?.remove();
        defaultPopup.current = null;
    };

    const setPopup = (feature) => {
        if (!map.current) return;
        if (defaultPopup.current) removePopup();

        if (!feature) {
            if (selectedEvent) selectedEvent(latLngClicked.current);
            return;
        }

        const popup = new mapboxgl.Popup({
            maxWidth: 'none',
            closeButton: false,
            focusAfterOpen: false,
            offset: feature.geometry.type === 'Point' ? [0, -75 / 2] : [0, 0],
        });

        const popupContent = getPopupContentByService(feature, service);
        if (!popupContent) return;

        const coords = feature.geometry?.coordinates;
        if (!coords?.length) return;

        const latLng = feature.geometry.type === 'Point' ? coords : latLngClicked.current;

        popup.setDOMContent(popupContent).setLngLat(latLng).addTo(map.current);

        defaultPopup.current = popup;

        if (selectedEvent) selectedEvent(latLngClicked.current);
    };

    useEffect(() => {
        setPopup(selectedFeature);
    }, [selectedFeature]);

    const setMapSource = (sourceName, data) => {
        if (!map.current || !data?.features?.length) return;
        const mapSource = map.current.getSource(sourceName);
        if (!mapSource) return;
        mapSource.setData(data);
    };

    useEffect(() => setMapSource(permitSources.point, point), [point, isMapLoaded]);
    useEffect(() => setMapSource(permitSources.points, points), [points, isMapLoaded]);
    useEffect(() => setMapSource(permitSources.lines, lines), [lines, isMapLoaded]);
    useEffect(
        () => setMapSource(permitSources.polygons, polygons),
        [polygons, isMapLoaded]
    );
    useEffect(
        () => setMapSource(permitSources.highlightedLines, highlightedLines),
        [highlightedLines]
    );

    return <LoadingSpinner mapRef={ref} isMapLoaded={isMapLoaded} />;
}

PermitsMap.propTypes = {
    point: PropTypes.object,
    points: PropTypes.object,
    lines: PropTypes.object,
    polygons: PropTypes.object,
    highlightedLines: PropTypes.object,
    config: PropTypes.object,
    sourceConfig: PropTypes.object,
    center: PropTypes.array,
    zoom: PropTypes.number,
    service: PropTypes.string.isRequired,
    mapContainer: PropTypes.string,
    selectedEvent: PropTypes.func,
    onFullScreen: PropTypes.func,
    featureName: PropTypes.string,
};

export default PermitsMap;
